import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getAllEnvelopes, createEnvelope, updateEnvelope, deleteEnvelope } from '@/services/api';
import type { Envelope, EnvelopeCreate } from '@/types';
import EnvelopeEditor from '@/components/EnvelopeEditor';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
    const [loading, setLoading] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    const [editingEnvelope, setEditingEnvelope] = useState<Envelope | null>(null);

    // Fetch envelopes on mount
    useEffect(() => {
        console.log('📦 AdminDashboard mounted, fetching envelopes...');
        fetchEnvelopes();
    }, []);

    const fetchEnvelopes = async () => {
        try {
            console.log('📦 Fetching envelopes...');
            setLoading(true);
            const response = await getAllEnvelopes(1, 50);
            console.log('✅ Envelopes fetched:', response.items.length, 'items');
            setEnvelopes(response.items);
        } catch (error) {
            console.error('❌ Failed to fetch envelopes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingEnvelope(null);
        setShowEditor(true);
    };

    const handleEdit = (envelope: Envelope) => {
        setEditingEnvelope(envelope);
        setShowEditor(true);
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this envelope?')) return;
        try {
            await deleteEnvelope(id);
            await fetchEnvelopes();
        } catch (error) {
            console.error('Failed to delete envelope:', error);
            alert('Failed to delete envelope');
        }
    };

    const handleTogglePublish = async (envelope: Envelope) => {
        try {
            console.log('📢 Toggling publish status for:', envelope.title);
            await updateEnvelope(envelope.id, {
                ...envelope,
                is_published: !envelope.is_published
            });
            console.log('✅ Publish status toggled');
            await fetchEnvelopes();
        } catch (error) {
            console.error('❌ Failed to toggle publish status:', error);
            alert('Failed to update publish status');
        }
    };

    const handleSave = async (data: EnvelopeCreate) => {
        try {
            console.log('💾 Saving envelope...', editingEnvelope ? 'UPDATE' : 'CREATE');
            if (editingEnvelope) {
                await updateEnvelope(editingEnvelope.id, data);
                console.log('✅ Envelope updated');
            } else {
                const newEnvelope = await createEnvelope(data);
                console.log('✅ Envelope created:', newEnvelope);
            }
            setShowEditor(false);
            setEditingEnvelope(null);
            console.log('🔄 Refreshing envelope list...');
            await fetchEnvelopes();
        } catch (error) {
            console.error('❌ Failed to save envelope:', error);
            throw error;
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-vintage-cream paper-texture">
            {/* Header */}
            <header className="bg-vintage-paper shadow-vintage">
                <div className="container mx-auto px-4 py-6 flex justify-between items-center">
                    <h1 className="text-3xl font-serif font-bold text-vintage-ink">Admin Dashboard</h1>
                    <div className="flex items-center space-x-4">
                        <button onClick={() => navigate('/')} className="btn-secondary text-sm">
                            View Site
                        </button>
                        <button onClick={handleLogout} className="btn-secondary text-sm">
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 py-8">
                {/* Action Bar */}
                <div className="mb-8 flex justify-between items-center">
                    <h2 className="text-2xl font-serif font-semibold text-vintage-darkbrown">
                        Manage Envelopes ({envelopes.length})
                    </h2>
                    <button onClick={handleCreate} className="btn-primary">
                        + Create New Envelope
                    </button>
                </div>

                {/* Envelope List */}
                {loading ? (
                    <div className="text-center py-12">
                        <p className="text-vintage-brown font-serif">Loading...</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {envelopes.map((envelope) => (
                            <div
                                key={envelope.id}
                                className="bg-vintage-paper rounded-lg shadow-vintage p-6 border border-vintage-brown/20"
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h3 className="text-xl font-serif font-bold text-vintage-ink">
                                                {envelope.title}
                                            </h3>
                                            <span
                                                className={`px-3 py-1 text-xs font-serif rounded-full ${envelope.is_published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                    }`}
                                            >
                                                {envelope.is_published ? 'Published' : 'Draft'}
                                            </span>
                                            <span className="px-3 py-1 text-xs font-serif bg-vintage-brown/10 text-vintage-darkbrown rounded-full">
                                                {envelope.style}
                                            </span>
                                        </div>
                                        <p className="text-sm text-vintage-brown font-body mb-2">
                                            Slug: <code className="bg-vintage-brown/10 px-2 py-1 rounded">{envelope.slug}</code>
                                        </p>
                                        <p className="text-sm text-vintage-darkbrown font-body line-clamp-2">
                                            {envelope.body.substring(0, 150)}...
                                        </p>
                                        <p className="text-xs text-vintage-brown mt-2">
                                            Updated: {new Date(envelope.updated_at).toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        <button
                                            onClick={() => window.open(`/envelope/${envelope.slug}`, '_blank')}
                                            className="btn-secondary text-sm py-2"
                                            title="Preview"
                                        >
                                            👁 Preview
                                        </button>
                                        <button 
                                            onClick={() => handleTogglePublish(envelope)} 
                                            className={`text-sm py-2 px-4 rounded font-serif ${envelope.is_published ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
                                            title={envelope.is_published ? 'Unpublish' : 'Publish'}
                                        >
                                            {envelope.is_published ? '📦 Unpublish' : '📢 Publish'}
                                        </button>
                                        <button onClick={() => handleEdit(envelope)} className="btn-primary text-sm py-2">
                                            ✏️ Edit
                                        </button>
                                        <button onClick={() => handleDelete(envelope.id)} className="btn-danger text-sm py-2">
                                            🗑 Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {envelopes.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-vintage-darkbrown font-serif text-lg">No envelopes yet.</p>
                                <p className="text-vintage-brown font-body mt-2">Create your first love letter!</p>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Editor Modal */}
            {showEditor && (
                <EnvelopeEditor
                    envelope={editingEnvelope}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowEditor(false);
                        setEditingEnvelope(null);
                    }}
                />
            )}
        </div>
    );
};

export default AdminDashboard;
